(() => {
  if (customElements.get('leslis-instock')) return;
  class LeslisInstock extends HTMLElement {
    connectedCallback() {
      this.life?.abort();
      this.life = new AbortController();
      this.queue = [];
      this.activeRequests = 0;
      this.cache = new Map();
      this.pending = new Map();
      this.version = 0;
      this.epoch = 0;
      this.page = 1;
      this.rows = [];
      this.indexLoaded = false;
      this.pageSize = Math.max(4, Math.min(16, Number(this.dataset.pageSize) || 8));
      const root = this.dataset.root || window.Shopify?.routes?.root || '/';
      this.root = root.startsWith('/') && !root.startsWith('//') ? root.replace(/\/?$/, '/') : '/';
      this.grid = this.querySelector('[data-stock-grid]');
      this.search = this.querySelector('[data-stock-search]');
      this.multiple = this.querySelector('[data-stock-multiple]');
      this.refreshButton = this.querySelector('[data-stock-refresh]');
      const options = { signal: this.life.signal };
      this.search.addEventListener('input', () => {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => { this.page = 1; if (this.indexLoaded) this.render(); }, 150);
      }, options);
      this.multiple.addEventListener('change', () => { this.page = 1; if (this.indexLoaded) this.render(); }, options);
      this.refreshButton.addEventListener('click', () => this.load(), options);
      this.querySelector('[data-stock-previous]').addEventListener('click', () => {
        this.page = Math.max(1, this.page - 1); this.render();
      }, options);
      this.querySelector('[data-stock-next]').addEventListener('click', () => { this.page += 1; this.render(); }, options);
      this.load();
    }

    disconnectedCallback() {
      this.version += 1;
      this.life?.abort();
      clearTimeout(this.searchTimer);
      this.queue?.splice(0).forEach((job) => job.reject(new Error('Disconnected')));
    }

    state(name, visible) {
      const node = this.querySelector('[data-stock-' + name + ']');
      if (node) node.hidden = !visible;
    }

    busy(value) {
      this.setAttribute('aria-busy', String(value));
      this.refreshButton.disabled = value;
      this.state('loading', value);
    }

    async fetch(url, accept, external = false) {
      const controller = new AbortController();
      const abort = () => controller.abort();
      this.life.signal.addEventListener('abort', abort, { once: true });
      const timer = setTimeout(abort, 10000);
      try {
        const response = await window.fetch(url, {
          method: 'GET', headers: { Accept: accept },
          credentials: external ? 'omit' : 'same-origin', signal: controller.signal
        });
        if (!response.ok) throw new Error('Unavailable');
        return accept === 'application/json' ? await response.json() : await response.text();
      } finally {
        clearTimeout(timer);
        this.life.signal.removeEventListener('abort', abort);
      }
    }

    normalize(data) {
      if (!data || !Array.isArray(data.variants) || (data.status && data.status !== 'ACTIVE')) throw new Error('Invalid stock response');
      const seen = new Set();
      return data.variants.flatMap((row) => {
        const id = String(row.variant_id || '');
        let handle = String(row.product_handle || '');
        if (!handle && row.storefront_url) {
          try { handle = decodeURIComponent(new URL(row.storefront_url).pathname.match(/\/products\/([^/]+)\/?$/)?.[1] || ''); } catch {}
        }
        const quantity = Number(row.available_quantity);
        if (!/^\d{1,25}$/.test(id) || !handle || /[\s/\\?#\u0000-\u001f]/.test(handle) || handle === '.' || handle === '..' ||
          !Number.isFinite(quantity) || quantity <= 0 || seen.has(id) || (row.product_status && row.product_status !== 'ACTIVE')) return [];
        seen.add(id);
        const title = String(row.product_title || '').slice(0, 200);
        const options = [row.option_1_value, row.option_2_value, row.option_3_value].filter(Boolean).join(' · ');
        return [{
          id, handle, quantity, title,
          variant: String(options || row.variant_title || '').slice(0, 250),
          sku: String(row.variant_sku || '').slice(0, 100),
          search: [title, options, row.variant_title, row.variant_sku, handle].join(' ').normalize('NFKC').toLocaleLowerCase()
        }];
      });
    }

    productUrl(handle, id, view = false) {
      const url = new URL(this.root + 'products/' + encodeURIComponent(handle), location.origin);
      url.searchParams.set('variant', id);
      if (view) url.searchParams.set('view', 'leslis-instock');
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview && /^\d+$/.test(preview)) url.searchParams.set('preview_theme_id', preview);
      return url;
    }

    async load() {
      const version = ++this.version;
      this.epoch += 1;
      this.cache.clear();
      this.pending.clear();
      this.page = 1;
      this.indexLoaded = false;
      this.grid.replaceChildren();
      ['error', 'partial', 'empty', 'page-empty', 'pagination'].forEach((key) => this.state(key, false));
      this.querySelector('[data-stock-count]').textContent = '';
      this.busy(true);
      try {
        const url = new URL(this.dataset.endpoint);
        if (url.protocol !== 'https:') throw new Error('Invalid endpoint');
        url.searchParams.set('shop', this.dataset.shop);
        const data = await this.fetch(url, 'application/json', true);
        if (version !== this.version || !this.isConnected) return;
        this.rows = this.normalize(data);
        this.indexLoaded = true;
        this.sourcePartial = Number.isFinite(Number(data.count)) && Number(data.count) > data.variants.length;
        await this.render();
      } catch {
        if (version !== this.version || !this.isConnected) return;
        this.rows = [];
        this.state('error', true);
        this.busy(false);
      }
    }

    enqueue(run) {
      return new Promise((resolve, reject) => {
        this.queue.push({ run, resolve, reject });
        this.drain();
      });
    }

    drain() {
      while (this.activeRequests < 3 && this.queue.length && !this.life.signal.aborted) {
        const job = this.queue.shift();
        this.activeRequests += 1;
        Promise.resolve().then(job.run).then(job.resolve, job.reject).finally(() => {
          this.activeRequests -= 1;
          this.drain();
        });
      }
    }

    parseProduct(html) {
      const document = new DOMParser().parseFromString(html, 'text/html');
      const template = document.querySelector('template[data-leslis-instock-product]');
      if (!template || !template.dataset.productId) throw new Error('Missing stock view');
      const variants = new Map();
      template.content.querySelectorAll('[data-stock-variant]').forEach((node) => {
        if (!/^\d+$/.test(node.dataset.id || '')) return;
        variants.set(node.dataset.id, { ...node.dataset });
      });
      return { title: template.dataset.title || '', variants };
    }

    async getProduct(row, exact = false) {
      const cached = this.cache.get(row.handle);
      if (cached?.variants.has(row.id)) return cached;
      const key = row.handle + (exact ? ':' + row.id : '');
      if (!this.pending.has(key)) {
        const epoch = this.epoch;
        const pending = this.enqueue(async () => {
          const html = await this.fetch(this.productUrl(row.handle, row.id, true), 'text/html');
          const result = this.parseProduct(html);
          if (epoch === this.epoch) {
            const previous = this.cache.get(row.handle);
            previous?.variants.forEach((value, id) => { if (!result.variants.has(id)) result.variants.set(id, value); });
            this.cache.set(row.handle, result);
          }
          return result;
        }).finally(() => { if (this.pending.get(key) === pending) this.pending.delete(key); });
        this.pending.set(key, pending);
      }
      const result = await this.pending.get(key);
      if (!result.variants.has(row.id) && !exact) return this.getProduct(row, true);
      return result;
    }

    element(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text) node.textContent = text;
      return node;
    }

    imageUrl(value) {
      if (!value || !String(value).trim()) return null;
      try {
        const url = new URL(value, location.origin);
        if (url.protocol !== 'https:' || (url.origin !== location.origin && url.hostname !== 'cdn.shopify.com')) return null;
        return url.href;
      } catch {
        return null;
      }
    }

    card(row, product, variant) {
      const verified = Boolean(product && variant);
      if (verified && variant.ready !== 'true') return null;
      const card = this.element('article', 'leslis-instock__card');
      const href = this.productUrl(row.handle, row.id).href;
      card.dataset.variantId = row.id;
      const title = product?.title || row.title;
      const imageUrl = verified && this.imageUrl(variant.image);
      if (imageUrl) {
        const imageLink = this.element('a', 'leslis-instock__image');
        imageLink.href = href; imageLink.tabIndex = -1; imageLink.setAttribute('aria-hidden', 'true');
        const image = this.element('img');
        image.src = imageUrl; image.alt = variant.imageAlt || title; image.loading = 'lazy'; image.decoding = 'async';
        image.width = 720; image.height = 720; image.dataset.leslisAi = variant.ai === '1' ? '1' : '0';
        imageLink.append(image); card.append(imageLink);
      }
      const body = this.element('div', 'leslis-instock__body');
      if (verified) body.append(this.element('span', 'leslis-instock__badge', this.dataset.badge));
      const heading = this.element('h2');
      const titleLink = this.element('a', '', title);
      titleLink.href = href; heading.append(titleLink); body.append(heading);
      const variantTitle = variant?.title || row.variant;
      if (variantTitle && variantTitle !== 'Default Title') body.append(this.element('p', 'leslis-instock__variant', variantTitle));
      if (variant?.sku || row.sku) body.append(this.element('p', 'leslis-instock__sku', variant?.sku || row.sku));
      if (verified && variant.price) {
        const price = this.element('div', 'leslis-instock__price');
        price.append(this.element('span', '', variant.price));
        if (variant.comparePrice) price.append(this.element('s', '', variant.comparePrice));
        body.append(price);
      } else {
        body.append(this.element('p', 'leslis-instock__notice', this.dataset.unverifiedNote));
      }
      const link = this.element('a', 'leslis-instock__link');
      const label = this.element('span', 'leslis-instock__link-label', verified ? this.dataset.view : this.dataset.unverified);
      const arrow = this.element('span', 'leslis-instock__arrow', '→');
      arrow.setAttribute('aria-hidden', 'true');
      link.append(label, arrow);
      link.href = href; body.append(link); card.append(body);
      return card;
    }

    async render() {
      const version = ++this.version;
      const query = this.search.value.trim().normalize('NFKC').toLocaleLowerCase();
      const rows = this.rows.filter((row) => (!this.multiple.checked || row.quantity > 1) && (!query || row.search.includes(query)));
      const pages = Math.max(1, Math.ceil(rows.length / this.pageSize));
      this.page = Math.min(this.page, pages);
      const group = rows.slice((this.page - 1) * this.pageSize, this.page * this.pageSize);
      this.grid.replaceChildren();
      ['error', 'empty', 'page-empty', 'partial'].forEach((key) => this.state(key, false));
      this.querySelector('[data-stock-count]').textContent = '';
      this.state('pagination', pages > 1);
      this.querySelector('[data-stock-previous]').disabled = this.page === 1;
      this.querySelector('[data-stock-next]').disabled = this.page === pages;
      this.querySelector('[data-stock-page-status]').textContent = this.dataset.pageLabel.replace('__CURRENT__', this.page).replace('__TOTAL__', pages);
      this.busy(group.length > 0);
      if (!group.length) {
        this.state('empty', true);
        this.busy(false);
        return;
      }
      const result = await Promise.all(group.map(async (row) => {
        try {
          const product = await this.getProduct(row);
          const variant = product.variants.get(row.id);
          return variant ? { row, product, variant } : { row, missing: true };
        } catch {
          return { row, failed: true };
        }
      }));
      if (version !== this.version || !this.isConnected) return;
      const fragment = document.createDocumentFragment();
      let shown = 0;
      result.forEach(({ row, product, variant, missing }) => {
        if (missing) return;
        const card = this.card(row, product, variant);
        if (card) { fragment.append(card); shown += 1; }
      });
      this.grid.append(fragment);
      this.querySelector('[data-stock-count]').textContent = shown === 1 && this.dataset.countLabelOne ? this.dataset.countLabelOne : this.dataset.countLabel.replace('__COUNT__', shown);
      this.state('partial', this.sourcePartial || result.some((entry) => entry.failed));
      this.state('page-empty', shown === 0);
      this.busy(false);
    }
  }
  customElements.define('leslis-instock', LeslisInstock);
})();
